import { cn } from "@pax/ui";
import { X } from "lucide-react";
import { useEffect } from "react";

const AUTO_DISMISS_MS = 4_000;

export type CheckoutReturnNoticeKind =
  | "paid"
  | "processing"
  | "expired"
  | "cancelled"
  | "auto_top_up_enabled"
  | "payment_failed"
  | "auto_top_up_pending"
  | "auto_top_up_refused"
  | "unconfirmed";

function noticeMessage(kind: CheckoutReturnNoticeKind): string {
  switch (kind) {
    case "auto_top_up_enabled":
      return "Auto top-up is on.";
    case "auto_top_up_pending":
      return "Stripe is still verifying your payment method. Auto top-up turns on by itself once it is verified.";
    case "auto_top_up_refused":
      return "Auto top-up couldn't be turned on with that payment method, so it is off. Use a card, bank account or Link.";
    case "paid":
      return "Payment complete. You can close this tab.";
    case "processing":
      return "Credits added. Your payment is still clearing, and they are removed if it fails.";
    case "payment_failed":
      return "We couldn't complete your payment, so no credits were added.";
    case "expired":
      return "This checkout link is too old to add credits here. Your balance is unchanged.";
    case "cancelled":
      return "Checkout cancelled. You can close this tab.";
    case "unconfirmed":
      return "We couldn't confirm your payment. Your credits will show up if it went through.";
  }
}

/** Bottom pill after Stripe returns. Sits above the game dock when one is showing. */
export function CheckoutReturnNotice({
  kind,
  onDismiss,
}: {
  kind: CheckoutReturnNoticeKind;
  onDismiss: () => void;
}) {
  const message = noticeMessage(kind);

  useEffect(() => {
    const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [kind, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed left-1/2 z-50 max-w-[calc(100vw-2rem)] -translate-x-1/2",
        "bottom-[calc(env(safe-area-inset-bottom,0px)+var(--app-bottom-chrome,0px)+1rem)]",
      )}
    >
      <button
        type="button"
        aria-label={`${message} Dismiss.`}
        onClick={onDismiss}
        className="pointer-events-auto flex w-full items-center gap-2 rounded-full bg-foreground py-2 pr-2 pl-4 text-left text-sm text-background shadow-lg"
      >
        <span className="min-w-0 flex-1 text-pretty">{message}</span>
        <span
          aria-hidden
          className="grid size-7 shrink-0 place-items-center rounded-full bg-background/15"
        >
          <X className="size-3.5" />
        </span>
      </button>
    </div>
  );
}
